<template>
  <div class="i-live-coach">
    <div class="top">
      <img
        :src="isMdOrLarger ? imageLinda : imageLindaMob"
        class="linda"
        alt="Linda Coaching"
      />
      <div class="info">
        <div class="head">
          Supercharge your career<br v-if="isMdOrLarger" />
          with live career coaching
        </div>
        Focused, weekly sessions that turn exploration into action.
      </div>
    </div>
    <div class="bottom">
      <div class="text">
        Get 4 hours of live career coaching a month — for less than <span class="highlight">$1</span>/day.
      </div>
      <div class="grid">
        <template
          v-for="perk in PERKS"
          :key="perk.icon"
        >
          <div
            v-if="isMdOrLarger || perk.alwaysShow"
            class="card"
          >
            <img :src="perk.icon" />
            <div class="perk">
              <div class="main">{{ perk.title }}</div>
              {{ perk.text }}
            </div>
          </div>
        </template>
      </div>
      <div class="note">
        <img :src="iconUser" /> <strong>99%</strong> <span>received a job interview within 3 months.</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { breakpointMd } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"

// ASSETS
import imageLinda from "@/assets/welcome/linda-coaching.avif"
import imageLindaMob from "@/assets/welcome/linda-coaching-mob.avif"
import icon1 from "@/assets/icons/icon-welcome-coach-gradient.svg"
import icon4 from "@/assets/icons/icon-welcome-salary-gradient.svg"
import icon6 from "@/assets/icons/icon-welcome-resume-gradient.svg"
import icon3 from "@/assets/icons/icon-welcome-career-gradient.svg"
import icon5 from "@/assets/icons/icon-welcome-interview-gradient.svg"
import icon2 from "@/assets/icons/icon-welcome-linkedin-gradient.svg"
import iconUser from "@/assets/icons/icon-user-speak.svg"

// EMITS
const emits = defineEmits(["scrollToPaymentTable"])

// COMPOSABLES
const isMdOrLarger = useIsScreenSize(breakpointMd)

// CONSTANTS
const PERKS = [
  { icon: icon1, title: "Weekly Focus Sessions", text: "One clear next step every time." },
  {
    icon: icon2,
    title: "Resume & LinkedIn Tear‑Downs",
    text: "Learn from live critiques + checklist.",
    alwaysShow: true
  },
  { icon: icon3, title: "Curated Role Roundup", text: "Best openings of the week, explained." },
  {
    icon: icon4,
    title: "Salary & Offer Strategy Clinic",
    text: "Share counters, leave with winning scripts.",
    alwaysShow: true
  },
  { icon: icon5, title: "Interview Drill Circles", text: "Practice answers, get coached feedback.", alwaysShow: true },
  { icon: icon6, title: "Application Sprint Workshops", text: "Build stronger apps together, fast.", alwaysShow: true }
]
</script>

<style lang="scss" scoped>
.i-live-coach {
  @include flex(column, start, center, $gap: 25px);
  @include border-radius-lg;

  background-image: url("@/assets/confirm/bg-gradient-multi.png");
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;

  @media screen and (min-width: $breakpoint-md) {
    gap: 35px;
  }

  .top {
    @include flex(column, start, center);

    position: relative;
    padding: 30px 15px 0;

    @media screen and (min-width: $breakpoint-md) {
      padding: 0;
    }

    .linda {
      width: 100%;
      max-width: 500px;

      @media screen and (min-width: $breakpoint-md) {
        max-width: unset;
      }
    }

    .info {
      @include text-md;

      position: absolute;
      color: $white;
      top: 45px;
      text-align: center;
      width: 90%;
      font-weight: 300;

      @media screen and (min-width: $breakpoint-md) {
        @include text-xl;

        font-weight: 400;
        width: 100%;
        text-align: start;
        top: unset;
        bottom: 30px;
        left: 30px;
      }

      @media screen and (min-width: $breakpoint-lg) {
        @include display-xs;
      }

      @media screen and (min-width: $breakpoint-xl) {
        @include display-xs;

        bottom: 60px;
        left: 60px;
      }

      .head {
        @include display-xs;

        font-weight: 500;
        margin-bottom: 10px;

        @media screen and (min-width: $breakpoint-md) {
          @include display-lg;

          font-weight: 700;
          margin-bottom: unset;
        }

        @media screen and (min-width: $breakpoint-lg) {
          @include display-lg;
        }

        @media screen and (min-width: $breakpoint-xl) {
          @include display-xxl;
        }
      }
    }
  }

  .bottom {
    @include flex(column, start, stretch, $gap: 25px);

    padding: 0 15px 30px;

    @media screen and (min-width: $breakpoint-xl) {
      align-items: start;
    }

    .text {
      @include text-xl;

      text-align: center;

      @media screen and (min-width: $breakpoint-md) {
        text-align: start;
      }

      @media screen and (min-width: $breakpoint-lg) {
        @include display-xs;
      }

      .highlight {
        background-image: $blue-purple-gradient;
        color: transparent;
        background-clip: text;
        font-weight: 700;
      }
    }

    .grid {
      @include grid($cols: 1fr, $rows: auto, $align-items: center, $gap: 20px);

      text-align: start;

      @media screen and (min-width: $breakpoint-md) {
        @include grid($cols: repeat(2, 1fr), $rows: repeat(3, 1fr), $align-items: stretch, $gap: 20px);
      }

      @media screen and (min-width: $breakpoint-xl) {
        @include grid($cols: repeat(3, 1fr), $rows: repeat(2, 1fr), $align-items: stretch, $gap: 20px);
      }

      .card {
        @include flex(column, space-between, start, $gap: 20px);
        @include border-radius-md;

        background-color: $white;
        padding: 15px;

        .perk {
          @include text-md;

          .main {
            @include text-xl;

            color: $amethyst600;
            font-weight: 700;
          }
        }
      }
    }

    .note {
      @include text-xs;

      margin-top: 10px;
      white-space: normal;
      text-align: center;

      @media screen and (min-width: $breakpoint-sm) {
        @include text-md;
      }

      @media screen and (min-width: $breakpoint-md) {
        text-align: start;
      }
    }
  }
}
</style>
